// frontend/src/components/bd/bubbles/svgBuilder.js
// Miroir EXACT de backend/bd/bubbles/svg.py + metrics.py — toute modif doit
// être répercutée là-bas (tests golden des deux côtés garantissent la parité
// au pixel).
export const FONT_FAMILY = "Patrick Hand";
const PAD_X = 16;
const TOP_BOTTOM_PAD = 8;
export const BD_W = 1024;
export const BD_H = 1536;

// Métriques texte par kind — miroir exact de backend/bd/bubbles/metrics.py.
// `max` est le plafond de l'auto-fit, donc la taille effective d'une bulle courte.
// 39/44 = valeurs réglées au curseur par l'équipe avant d'être demandées par défaut.
// `font_size` reste le repli des planches d'avant l'auto-fit — ne pas le toucher.
const METRICS = {
  speech:    { font_size: 19, max: 39, min: 16, line_ratio: 1.26, base_ratio: 0.68 },
  narration: { font_size: 17, max: 44, min: 14, line_ratio: 1.24, base_ratio: 0.71 },
};
const _m = (kind) => METRICS[kind] || METRICS.speech;
export const fontSizeFor = (kind) => _m(kind).font_size;
export const maxFontFor = (kind) => _m(kind).max;
export const minFontFor = (kind) => _m(kind).min;
// round-half-up (= int(x+0.5) Python). La parité d'arrondi est verrouillée
// par la table exhaustive des tests Python/JS.
export const lineHFor = (kind, fontSize) => Math.round((fontSize ?? fontSizeFor(kind)) * _m(kind).line_ratio);
export const baselineFor = (kind, fontSize) => Math.round((fontSize ?? fontSizeFor(kind)) * _m(kind).base_ratio);
// Plafond de hauteur (fraction BD_H) avant rétrécissement — miroir MAX_H_FRAC.
export const MAX_H_FRAC = 0.20;

const RADIUS_SPEECH = 18;
const RADIUS_NARRATION = 4;
const SHADOW_DX = 0, SHADOW_DY = 3;
const SHADOW_FILL = "rgba(0,0,0,0.18)";
const STROKE = "#888888";
const STROKE_W = 2;
const FILL_SPEECH = "rgba(255,255,255,0.82)";
const FILL_NARRATION = "rgba(247,247,244,0.82)";
const TAIL_BASE_HALF = 12;
// Une pointe sert à désigner qui parle, pas à traverser la case. Au-delà de cette
// longueur elle barre le dessin ; on garde sa direction et on raccourcit le triangle.
// Relevé sur 442 pointes en base : médiane 135 px, p75 199, max 796.
const TAIL_MAX_LEN = 200;

const _r = (v) => Math.round(v);
const _esc = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
// Hauteur de boîte en px (parité avec box_height_px de metrics.py). Exportée
// pour que l'éditeur dimensionne la zone cliquable d'une bulle exactement sur
// son rendu. Défaut kind="speech" pour rétro-compat des appels existants.
export const boxHeightPx = (n, kind = "speech", fontSize) =>
  TOP_BOTTOM_PAD * 2 + Math.max(1, n) * lineHFor(kind, fontSize);

function bubbleSvg(b, w, h) {
  if (!(b.lines || []).some((ln) => (ln || "").trim())) return "";

  const left = _r(b.x * w);
  const top = _r(b.y * h);
  const boxW = _r(b.w * w);
  const fs = b.font_size != null ? b.font_size : fontSizeFor(b.kind);
  const boxH = boxHeightPx((b.lines || []).length, b.kind, fs);
  const radius = b.kind === "speech" ? RADIUS_SPEECH : RADIUS_NARRATION;
  const fill = b.kind === "speech" ? FILL_SPEECH : FILL_NARRATION;
  const parts = [];

  parts.push(
    `<rect x="${left + SHADOW_DX}" y="${top + SHADOW_DY}" width="${boxW}" height="${boxH}" rx="${radius}" fill="${SHADOW_FILL}"/>`
  );
  if (b.kind === "speech" && b.tail_x != null && b.tail_y != null) {
    const cx = _r(left + boxW / 2);
    const bottom = top + boxH;
    let ax = _r(b.tail_x * w), ay = _r(b.tail_y * h);
    // base de la pointe sur le bord face au tip : haut si le visage est
    // au-dessus de la bulle, bas sinon (évite la pointe qui traverse la boîte).
    // On tranche sur la cible NON arrondie : à 0,2 px près, un arrondi faisait
    // basculer la base du haut vers le bas et la pointe repartait à travers la boîte.
    const baseY = b.tail_y * h < top ? top : bottom;
    const dx = ax - cx, dy = ay - baseY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance > TAIL_MAX_LEN) {
      // Apex arrondi comme les autres points : la parité avec le rendu Python reste
      // une comparaison d'entiers, pas de représentations décimales.
      const scale = TAIL_MAX_LEN / distance;
      ax = _r(cx + dx * scale);
      ay = _r(baseY + dy * scale);
    }
    parts.push(
      `<polygon points="${cx - TAIL_BASE_HALF},${baseY} ${cx + TAIL_BASE_HALF},${baseY} ${ax},${ay}" fill="${fill}" stroke="${STROKE}" stroke-width="${STROKE_W}"/>`
    );
  }
  parts.push(
    `<rect x="${left}" y="${top}" width="${boxW}" height="${boxH}" rx="${radius}" fill="${fill}" stroke="${STROKE}" stroke-width="${STROKE_W}"/>`
  );
  // texte — métriques dérivées de la police de la bulle (auto-fit ; fallback legacy)
  // Dialogue (speech) : centré horizontalement dans la boîte (convention BD).
  // Narration : reste aligné à gauche (cartouche, convention différente).
  const isSpeech = b.kind === "speech";
  const tx = isSpeech ? _r(left + boxW / 2) : left + PAD_X;
  const anchorAttr = isSpeech ? ' text-anchor="middle"' : "";
  const baseline = baselineFor(b.kind, fs);
  const lineH = lineHFor(b.kind, fs);
  const tspans = (b.lines || [])
    .map((line, i) => `<tspan x="${tx}" y="${top + TOP_BOTTOM_PAD + baseline + i * lineH}">${_esc(line)}</tspan>`)
    .join("");
  parts.push(`<text font-family="${FONT_FAMILY}" font-size="${fs}"${anchorAttr} fill="#222222">${tspans}</text>`);
  return `<g>${parts.join("")}</g>`;
}

export function buildBubbleSvg(bubbles, w, h) {
  const body = (bubbles || []).map((b) => bubbleSvg(b, w, h)).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`;
}
